import { Alert, TextField } from '@mui/material'
import { AssertNeverError } from '@omnea-digital/toolbox'
import { useTranslation } from 'react-i18next'
import { AssigneeSourceType } from '@omnea-app/fe-users-and-groups/assignees/AssigneeAutocomplete'
import { SingleAssigneeAutocomplete } from '@omnea-app/fe-users-and-groups/assignees/SingleAssigneeAutocomplete'
import { SearchableDropdown } from '@omnea-app/omui/SearchableDropdown'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { useRoles } from '../useRoles'
import { useUserPropertyValues } from './useUserPropertyValues'
import type { AccessTab } from './accessDialogByEntity'
import type { RecordGrantSubject } from './useUpdateRecordAccessGrant'

type Props = {
  tab: AccessTab
  /** Subjects already in the tab's list, which the picker no longer offers. */
  hiddenSubjectIds: string[]
  disabled: boolean
  onAdd: (subject: RecordGrantSubject, name: string) => void
}

export const AddRecordAccessSubject = ({ tab, hiddenSubjectIds, disabled, onAdd }: Props) => {
  const { t } = useTranslation('settings', { keyPrefix: 'record-access' })
  const roles = useRoles({ enabled: tab === 'roles' })
  const userPropertyValues = useUserPropertyValues({ enabled: tab === 'user-attributes' })

  switch (tab) {
    case 'users-and-groups':
      return (
        <SingleAssigneeAutocomplete
          type={AssigneeSourceType.USERS_GROUPS}
          value={null}
          clearAfterSelection
          fullWidth
          disabled={disabled}
          label={t('add-users-and-groups-label')}
          hiddenAssigneeIds={hiddenSubjectIds}
          onChange={(selectedAssignee) => {
            // A typed address the picker could not resolve arrives as EMAIL with a minted id,
            // which names no user.
            if (selectedAssignee?.type === AssigneeTypeEnum.USER) {
              onAdd({ kind: 'user', userId: selectedAssignee.id }, selectedAssignee.name ?? selectedAssignee.value)
            }
            if (selectedAssignee?.type === AssigneeTypeEnum.GROUP) {
              onAdd({ kind: 'group', groupId: selectedAssignee.id }, selectedAssignee.name ?? selectedAssignee.value)
            }
          }}
        />
      )
    case 'roles':
      // An empty picker would read as an organisation with no roles.
      if (roles.isError) return <Alert severity="error">{t('picker-load-error')}</Alert>
      return (
        <SearchableDropdown
          value={null}
          options={(roles.data?.roles ?? []).filter((role) => !hiddenSubjectIds.includes(role.id))}
          onChange={(selectedRole) => {
            if (selectedRole != null) onAdd({ kind: 'role', roleId: selectedRole.id }, selectedRole.name)
          }}
          getOptionLabel={(role) => role.name}
          disabled={disabled || roles.isPending}
          renderInput={(params) => <TextField {...params} label={t('add-role-label')} />}
        />
      )
    case 'user-attributes':
      if (userPropertyValues.isError) return <Alert severity="error">{t('picker-load-error')}</Alert>
      return (
        <SearchableDropdown
          value={null}
          options={(userPropertyValues.data ?? []).filter((value) => !hiddenSubjectIds.includes(value.id))}
          onChange={(selectedValue) => {
            if (selectedValue != null) {
              onAdd({ kind: 'user_property', dimension: selectedValue.dimension, valueId: selectedValue.id }, selectedValue.name)
            }
          }}
          getOptionLabel={(value) => value.name}
          groupBy={(value) => t(`user-attribute.${value.dimension}`)}
          disabled={disabled || userPropertyValues.isPending}
          renderInput={(params) => <TextField {...params} label={t('add-user-attribute-label')} />}
        />
      )
    default:
      throw new AssertNeverError(tab)
  }
}
